import React, { useContext, useEffect, useState } from "react";
import axiosInstance from "../axiosConfig";
import { AuthContext } from "../AuthContext";
import Logo from "../assets/Img/Logo-1.png";
import User from "../assets/Img/User.gif";
import DataTable from "react-data-table-component";
import { PhotoCell, Lightbox } from "../components/PhotoViewer";

function UserTickets() {
  const { user, logout } = useContext(AuthContext);
  const [department, setDepartment] = useState("Not Assigned");
  const [ticketData, setTicketData] = useState([]);
  const [assignedTickets, setAssignedTickets] = useState([]);
  const [activeTicketTab, setActiveTicketTab] = useState("assigned");
  const [hover, setHover] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showModal, setShowModal] = useState(false);
  const [ticketForm, setTicketForm] = useState({
    UserID: "",
    SupervisorID: "",
    Department: "",
    Subject: "",
    TicketDescription: "",
    photo: null,
  });
  const [modalMessage, setModalMessage] = useState("");
  const [modalLoading, setModalLoading] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  // Store the list of supervisors for the user's department
  const [supervisors, setSupervisors] = useState([]);
  
  // pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;


  // Update isMobile on window resize.
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch all users to get the logged-in user's department.
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axiosInstance.get("/api/AllUsers");
        const users = response.data;
        const userDepartment = users.find(
          (u) => u.EmployeeID === user?.EmployeeID
        )?.Department;
        setDepartment(userDepartment || "Not Assigned");
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    if (user?.EmployeeID) {
      fetchUsers();
    }
  }, [user?.EmployeeID]);

  // Fetch tickets raised by the logged-in user.
  useEffect(() => {
    const fetchTickets = async () => {
      try {
        // Call GET API with the user's EmployeeID.
        const response = await axiosInstance.get(`/api/User-tickets/${user?.EmployeeID}`);
        setTicketData(response.data);
      } catch (error) {
        console.error("Error fetching tickets:", error);
      }
    };
    if (user?.EmployeeID) {
      fetchTickets();
    }
  }, [user?.EmployeeID]);

  // Tickets assigned by supervisor in Worker Ticket Management.
  // The API is filtered by the current logged-in worker's Employee ID.
  useEffect(() => {
    const fetchAssignedTickets = async () => {
      try {
        const response = await axiosInstance.get(
          `/api/worker-tickets/worker/${user?.EmployeeID}`
        );
        setAssignedTickets(response.data || []);
      } catch (error) {
        console.error("Error fetching assigned worker tickets:", error);
        setAssignedTickets([]);
      }
    };

    if (user?.EmployeeID) {
      fetchAssignedTickets();
    }
  }, [user?.EmployeeID]);

  // 3. Once we know the user's department, fetch the supervisors for that department.
  useEffect(() => {
    const fetchSupervisors = async () => {
      if (!department || department === "Not Assigned") return;

      try {
        const response = await axiosInstance.get(`/api/supervisor/${department}`);
        setSupervisors(response.data);
      } catch (error) {
        console.error("Error fetching supervisors:", error);
      }
    };

    fetchSupervisors();
  }, [department]);

  // Auto check session expiration.
  useEffect(() => {
    const checkSessionExpiration = () => {
      const sessionExpiration = localStorage.getItem("sessionExpiration");
      const currentTime = new Date();
      if (!sessionExpiration || currentTime > new Date(sessionExpiration)) {
        window.location.reload();
      }
    };
    checkSessionExpiration();
    const interval = setInterval(checkSessionExpiration, 5000);
    return () => clearInterval(interval);
  }, []);


  const showAlert = (message, type) => {
    const alertPlaceholder = document.getElementById("alertPlaceholder");
    const alertHTML = `
      <div class="alert alert-${type} alert-dismissible fade show" role="alert">
        <strong>${type === "success" ? "Success!" : "Error!"}</strong> ${message}
        <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
      </div>`;
    alertPlaceholder.innerHTML = alertHTML;
    setTimeout(() => {
      alertPlaceholder.innerHTML = "";
    }, 3000);
  };

  // Function to handle ticket confirmation via PATCH.
  const handleConfirm = async (ticketId) => {
    try {
      const response = await axiosInstance.patch(`/api/User-tickets/${ticketId}/confirm`);
      showAlert(response.data.message, "success");
      // Re-fetch tickets after confirming.
      const ticketsRes = await axiosInstance.get(`/api/User-tickets/${user?.EmployeeID}`);
      setTicketData(ticketsRes.data);
    } catch (error) {
      console.error("Error confirming ticket:", error);
      showAlert("Failed to confirm ticket.", "danger");
    }
  };



    // Open the modal and pre-populate the ticket form with user's ID & department
    const openModal = () => {
      setShowModal(true);
      setTicketForm({
        UserID: user?.EmployeeID || "",
        SupervisorID: "",
        Department: department,
        Subject: "",
        TicketDescription: "",
        photo: null,
      });
    };
      
    const closeModal = () => {
      setShowModal(false);
      setModalMessage("");
      setTicketForm({
        UserID: user?.EmployeeID || "",
        SupervisorID: "",
        Department: department,
        Subject: "",
        TicketDescription: "",
        photo: null,
      });
    };
  
    const handleModalInputChange = (e) => {
      const { name, value, files } = e.target;
      if (name === "photo") {
        setTicketForm({ ...ticketForm, [name]: files[0] });
      } else {
        setTicketForm({ ...ticketForm, [name]: value });
      }
    };
  
    const handleModalSubmit = async (e) => {
      e.preventDefault();
      setModalLoading(true);
      try {
        const formData = new FormData();
        Object.keys(ticketForm).forEach((key) => {
          if (ticketForm[key] !== null && ticketForm[key] !== "") {
            formData.append(key, ticketForm[key]);
          }
        });

        const response = await axiosInstance.post("/api/User-tickets", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        setModalMessage(response.data.message);
        closeModal();
        showAlert(response.data.message, "success");
        // Re-fetch tickets after successful creation.
        const ticketsRes = await axiosInstance.get(`/api/User-tickets/${user?.EmployeeID}`);
        setTicketData(ticketsRes.data);
      } catch (error) {
        console.error("Error creating ticket:", error);
        setModalMessage("Failed to create ticket.");
        showAlert("Failed to create ticket.", "danger");
      } finally {
        setModalLoading(false);
      }
    };
    // derive pagination values
    const totalPages = Math.ceil(ticketData.length / itemsPerPage);
    const paginatedData = ticketData.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );


  // Define columns for the DataTable.
  const columns = [
    {
      name: "Ticket ID",
      selector: (row) => row.TicketID,
      sortable: true,
      width: "100px"
    },
    {
      name: "Department",
      selector: (row) => row.Department,
      sortable: true
    },
    {
      name: "Subject",
      selector: (row) => row.Subject,
      sortable: true,
      wrap: true
    },
    {
      name: "Description",
      selector: (row) => row.TicketDescription,
      sortable: false,
      wrap: true,
      grow: 2
    },
    {
      name: "Status",
      selector: (row) => row.TicketStatus,
      sortable: true,
      width: "120px"
    },
    {
      name: "Photo",
      cell: (row) => <PhotoCell src={row.photo_path} label="Ticket Photo" onView={setLightbox} />,
      width: "100px"
    },
    {
      name: "Raised Date",
      selector: (row) => {
        const date = new Date(row.CreatedDate);
        return date.toLocaleDateString("en-GB");
      },
      sortable: true,
      width: "150px"
    },
    {
      name: "Solved by",
      selector: (row) => row.SolvedBy || "N/A",
      sortable: true,
      width: "120px"
    },
    {
        name: "Action",
        cell: (row) =>
          row.TicketStatus.toLowerCase() === "solved" && row.isConfirmed !== "Yes" ? (
            <button className="btn btn-sm btn-success" onClick={() => handleConfirm(row.TicketID)}>
              Confirm
            </button>
          ) : (
            ""
          ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
        width: "120px"
      }
  ];

  const ticketTableStyles = {
    headCells: {
      style: {
        backgroundColor: "#f5f8fc",
        color: "#475467",
        fontSize: "12px",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        paddingLeft: "16px",
        paddingRight: "16px",
      },
    },
    cells: {
      style: {
        color: "#26354a",
        paddingLeft: "16px",
        paddingRight: "16px",
        paddingTop: "10px",
        paddingBottom: "10px",
      },
    },
    rows: { style: { minHeight: "66px", borderBottom: "1px solid #edf1f7" } },
  };

//   // Open the modal and pre-populate the UserID and Department.
//   const openModal = () => {
//     setShowModal(true);
//     setTicketForm({
//       ...ticketForm,
//       UserID: user?.EmployeeID || "",
//       Department: department
//     });
//   };


  return (
    <div>
      <Lightbox src={lightbox} onClose={() => setLightbox(null)} />
      <div id="alertPlaceholder"></div>
      <nav
        className="navbar navbar-expand-lg bg-body-white"
        style={{ boxShadow: "0 1px 5px rgba(0, 0, 0, 0.2)" }}
      >
        <div className="container-fluid">
          <a className="navbar-brand">
            <img
              src={Logo}
              className="img-fluid rounded-top"
              alt="Logo"
              style={{ height: "40px", marginLeft: "10px" }}
            />
          </a>
          <button
            className="navbar-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarSupportedContent"
            aria-controls="navbarSupportedContent"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          <div className="collapse navbar-collapse" id="navbarSupportedContent">
            <ul className="navbar-nav me-auto mb-2 mb-lg-0">
              <li className="nav-item" style={{ marginLeft: "10px" }}>
                <a
                  className="nav-link active"
                  aria-current="page"
                  href="/User-dashboard"
                  onMouseEnter={() => setHover(true)}
                  onMouseLeave={() => setHover(false)}
                  style={{ color: hover ? "#ff0000" : "inherit" }}
                >
                  Home
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="collapse navbar-collapse" id="navbarSupportedContent">
          <ul className="navbar-nav me-auto mb-2 mb-lg-0"></ul>
          {/* <span style={{ marginLeft: isMobile ? "0" : "-21rem" }}>
            Welcome, {user?.Name}!{" "}
          </span> */}
          <div className="dropdown">
            <a
              className="d-flex align-items-center text-decoration-none dropdown-toggle ml-5"
              role="button"
              id="dropdownMenuLink"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              style={{
                marginRight: "20px",
                justifyContent: "end",
                marginLeft: "10px",
                ...(isMobile && { marginTop: "-20px" })
              }}
            >
              <img
                src={User}
                alt="Profile"
                className="rounded-circle"
                style={{ width: "40px", height: "40px", objectFit: "cover" }}
              />
            </a>
            <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="dropdownMenuLink">
              <li>
                <a className="dropdown-item text-danger" onClick={logout}>
                  Logout
                </a>
              </li>
            </ul>
          </div>
        </div>
      </nav>
      <div className="ticket-workspace">
        <main className="ticket-main">
          <div className="ticket-hero">
            <div>
              <span className="ticket-hero-eyebrow">WORKER SUPPORT CENTER</span>
              <h2>My Tickets</h2>
              <p>Check tickets raised by your supervisor and raise support requests from one place.</p>
            </div>
            <div className="ticket-worker-chip">
              <i className="bi bi-person-circle"></i>
              <div><small>Employee ID</small><strong>{user?.EmployeeID || "-"}</strong></div>
            </div>
          </div>

          <div className="ticket-summary-grid">
            <div className="ticket-summary-card blue"><i className="bi bi-clipboard-check"></i><div><small>Supervisor tickets</small><strong>{assignedTickets.length}</strong></div></div>
            <div className="ticket-summary-card purple"><i className="bi bi-send"></i><div><small>My raised tickets</small><strong>{ticketData.length}</strong></div></div>
            <div className="ticket-summary-card neutral"><i className="bi bi-building"></i><div><small>Department</small><strong>{department || "Not Assigned"}</strong></div></div>
          </div>

          <div className="ticket-content-card">

            <div className="ticket-tabs" role="tablist" aria-label="Ticket sections">
              <button
                type="button"
                className={`ticket-tab ${activeTicketTab === "assigned" ? "active" : ""}`}
                onClick={() => setActiveTicketTab("assigned")}
              >
                <i className="bi bi-clipboard-check me-2"></i>Supervisor Tickets
                <span>{assignedTickets.length}</span>
              </button>
              <button
                type="button"
                className={`ticket-tab ${activeTicketTab === "raised" ? "active" : ""}`}
                onClick={() => setActiveTicketTab("raised")}
              >
                <i className="bi bi-send me-2"></i>My Raised Tickets
                <span>{ticketData.length}</span>
              </button>
            </div>

            {activeTicketTab === "assigned" ? (
            <div className="ticket-list-shell">
              {assignedTickets.length === 0 ? (
                <div className="assigned-ticket-empty">
                  <i className="bi bi-check2-circle me-2"></i>No supervisor ticket available right now.
                </div>
              ) : !isMobile ? (
                <div className="table-responsive">
                  <table className="table assigned-ticket-table mb-0">
                    <thead>
                      <tr>
                        <th>Ticket</th>
                        <th>Assigned date</th>
                        <th>Reason</th>
                        <th>Lot no.</th>
                        <th>Supervisor</th>
                        <th>Photo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assignedTickets.map((ticket) => (
                        <tr key={ticket.id}>
                          <td><span className="ticket-id-badge">#{ticket.id}</span></td>
                          <td>{ticket.date ? new Date(ticket.date).toLocaleDateString("en-GB") : "-"}</td>
                          <td className="ticket-description">{ticket.desc || "-"}</td>
                          <td>{ticket.lot_id || "-"}</td>
                          <td>{ticket.updated_by || "Supervisor"}</td>
                          <td><PhotoCell src={ticket.photo_path} label="Work Ticket Photo" onView={setLightbox} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="assigned-ticket-cards">
                  {assignedTickets.map((ticket) => (
                    <div className="assigned-ticket-card" key={ticket.id}>
                      <div className="assigned-card-top">
                        <span className="ticket-id-badge">Ticket #{ticket.id}</span>
                        <span>{ticket.date ? new Date(ticket.date).toLocaleDateString("en-GB") : "-"}</span>
                      </div>
                      <div className="assigned-card-description"><strong>Reason:</strong> {ticket.desc || "No reason added."}</div>
                      <div className="assigned-card-details">
                        <span><strong>Lot:</strong> {ticket.lot_id || "-"}</span>
                        <span><strong>Assigned by:</strong> {ticket.updated_by || "Supervisor"}</span>
                      </div>
                      {ticket.photo_path && (
                        <div className="mt-2"><PhotoCell src={ticket.photo_path} label="Work Ticket Photo" onView={setLightbox} /></div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            ) : (
            <>
            <button onClick={openModal} className="btn btn-primary mb-3">
              <i className="bi bi-plus-circle me-2"></i>Create Ticket
            </button>
            {/* Show table for non-mobile devices, card layout for mobile */}
            {!isMobile ? (
              <DataTable
                columns={columns}
                data={ticketData}
                pagination
                highlightOnHover
                customStyles={ticketTableStyles}
                noDataComponent={<div className="py-5 text-muted">You have not raised any ticket yet.</div>}
              />
            ) : (
               <div className="table-mobile">
                {paginatedData.map((ticket, index) => (
                  <div key={index} className="raised-ticket-card">
                    <div className="card-body">
                      <div className="raised-ticket-card-top">
                        <h5 className="card-title mb-0">Ticket #{ticket.TicketID}</h5>
                        <span className={`raised-ticket-status ${String(ticket.TicketStatus || "").toLowerCase().replace(/\s+/g, "-")}`}>{ticket.TicketStatus}</span>
                      </div>
                      <p className="card-text"><strong>Subject:</strong> {ticket.Subject}</p>
                      <p className="card-text"><strong>Description:</strong> {ticket.TicketDescription}</p>
                      <div className="mb-2">
                        <strong>Photo: </strong> 
                        <div className="mt-1 d-inline-block">
                          <PhotoCell src={ticket.photo_path} label="Ticket Photo" onView={setLightbox} />
                        </div>
                      </div>
                      <p className="card-text"><strong>Solved By:</strong> {ticket.SolvedBy}</p>
                      <p className="card-text">
                        <strong>Date:</strong> {new Date(ticket.CreatedDate).toLocaleDateString("en-GB")}
                      </p>
                      {ticket.TicketStatus.toLowerCase() === "solved" && ticket.isConfirmed !== "Yes" && (
                        <button className="btn btn-sm btn-success" onClick={() => handleConfirm(ticket.TicketID)}>
                          Confirm
                        </button>
                      )}
                      {ticket.isConfirmed === "Yes" && ticket.Confirmed_Time && (
                        <p className="card-text">
                          <strong>Confirmed On:</strong> {new Date(ticket.Confirmed_Time).toLocaleDateString("en-GB")}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
                {/* pagination controls */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-between mt-4">
                    <button
                      className="btn btn-primary mb-3"
                      onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                    <span>Page {currentPage} of {totalPages}</span>
                    <button
                      className="btn btn-primary mb-3"
                      onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}
            </>
            )}
          </div>
        </main>
      </div>

      {/* Create Ticket Modal */}
      {showModal && (
        <div className="modal-overlay" role="presentation" onMouseDown={closeModal}>
          <div className="modal-content ticket-create-modal" role="dialog" aria-modal="true" aria-label="Create ticket" onMouseDown={(event) => event.stopPropagation()}>
            <div className="ticket-modal-header">
              <div className="ticket-modal-icon"><i className="bi bi-plus-circle"></i></div>
              <div className="ticket-modal-title-wrap">
                <h4>Create Support Ticket</h4>
                <p>Share the issue with your supervisor. Required fields are marked.</p>
              </div>
              <button type="button" className="ticket-modal-close" onClick={closeModal} aria-label="Close">&times;</button>
            </div>
            <form onSubmit={handleModalSubmit}>
              <div className="ticket-modal-body">
                <div className="ticket-readonly-grid">
                  <div className="ticket-readonly-field"><span>Employee ID</span><strong>{ticketForm.UserID || "-"}</strong></div>
                  <div className="ticket-readonly-field"><span>Department</span><strong>{ticketForm.Department || "Not assigned"}</strong></div>
                </div>
                <div className="ticket-form-field">
                  <label>Supervisor <em>*</em></label>
                  <select name="SupervisorID" value={ticketForm.SupervisorID} onChange={handleModalInputChange} className="form-control" required>
                    <option value="">Select your supervisor</option>
                    {supervisors.map((sup) => <option key={sup.EmployeeID} value={sup.EmployeeID}>{sup.Supervisor_Name}</option>)}
                  </select>
                </div>
                <div className="ticket-form-field">
                  <label>Subject <em>*</em></label>
                  <input type="text" name="Subject" value={ticketForm.Subject} onChange={handleModalInputChange} className="form-control" placeholder="Enter a short subject" required />
                </div>
                <div className="ticket-form-field">
                  <label>Describe the issue <em>*</em></label>
                  <textarea name="TicketDescription" value={ticketForm.TicketDescription} onChange={handleModalInputChange} className="form-control" placeholder="Write complete details so your supervisor can help faster..." required />
                </div>
                <div className="ticket-form-field">
                  <label>Photo <small>Optional</small></label>
                  <label className="ticket-upload-box">
                    <i className="bi bi-image"></i>
                    <span>{ticketForm.photo ? ticketForm.photo.name : "Attach a photo if it helps explain the issue"}</span>
                    <input type="file" name="photo" accept="image/*" onChange={handleModalInputChange} />
                  </label>
                </div>
              </div>
              <div className="ticket-modal-footer">
                <button type="button" className="btn ticket-cancel-button" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn ticket-submit-button" disabled={modalLoading}>
                  <i className={`bi ${modalLoading ? "bi-arrow-repeat" : "bi-send"} me-2`}></i>{modalLoading ? "Submitting..." : "Submit Ticket"}
                </button>
              </div>
            </form>
            {modalMessage && <div className="alert alert-info">{modalMessage}</div>}
          </div>
        </div>
      )}

      {/* Inline Responsive CSS */}
      <style jsx="true">{`
        .ticket-workspace { min-height: calc(100vh - 72px); background: #f3f6fb; padding: 28px 18px 48px; }
        .ticket-main { max-width: 1180px; margin: 0 auto; }
        .ticket-hero { min-height: 160px; background: linear-gradient(115deg, #102b58, #245ea8 62%, #5284c7); border-radius: 18px; color: #fff; padding: 28px 32px; display: flex; justify-content: space-between; align-items: center; gap: 20px; box-shadow: 0 12px 30px rgba(27, 70, 128, .22); }
        .ticket-hero-eyebrow { display: block; font-size: 11px; font-weight: 700; letter-spacing: .14em; color: #b8d8ff; margin-bottom: 7px; }
        .ticket-hero h2 { font-size: 32px; margin: 0; font-weight: 750; }
        .ticket-hero p { margin: 7px 0 0; color: #e2efff; font-size: 15px; }
        .ticket-worker-chip { display: flex; align-items: center; gap: 10px; padding: 12px 16px; border: 1px solid rgba(255,255,255,.35); background: rgba(255,255,255,.12); border-radius: 12px; min-width: 145px; }
        .ticket-worker-chip i { font-size: 26px; }
        .ticket-worker-chip small { display: block; color: #d8eaff; font-size: 11px; }
        .ticket-worker-chip strong { font-size: 16px; }
        .ticket-summary-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; margin: 18px 0; }
        .ticket-summary-card { border-radius: 14px; padding: 17px; display: flex; align-items: center; gap: 12px; background: #fff; border: 1px solid #e5ebf5; box-shadow: 0 4px 14px rgba(37, 62, 102, .06); }
        .ticket-summary-card i { width: 42px; height: 42px; border-radius: 12px; display: inline-flex; align-items: center; justify-content: center; font-size: 20px; }
        .ticket-summary-card small { display: block; color: #667085; font-size: 12px; }
        .ticket-summary-card strong { display: block; color: #20314a; margin-top: 3px; font-size: 18px; text-transform: capitalize; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 205px; }
        .ticket-summary-card.blue i { color: #1d65bb; background: #e7f1ff; }.ticket-summary-card.purple i { color: #7048cf; background: #f0eaff; }.ticket-summary-card.neutral i { color: #d97706; background: #fff4dd; }
        .ticket-content-card { background: #fff; border-radius: 16px; padding: 20px; box-shadow: 0 7px 22px rgba(37, 62, 102, .08); border: 1px solid #e4eaf3; }
        .ticket-tabs { display: flex; gap: 8px; border-bottom: 1px solid #dfe7f2; margin-bottom: 20px; }
        .ticket-tab {
          border: 0;
          border-bottom: 3px solid transparent;
          background: transparent;
          color: #667085;
          padding: 12px 16px 11px;
          font-weight: 600;
          white-space: nowrap;
        }
        .ticket-tab.active { color: #245ea8; border-bottom-color: #245ea8; }
        .ticket-tab span { margin-left: 8px; min-width: 22px; height: 22px; display: inline-flex; align-items: center; justify-content: center; border-radius: 20px; background: #e8f0fb; color: #245ea8; font-size: 12px; }
        .ticket-list-shell {
          background: #ffffff;
          border: 1px solid #dbe5f3;
          border-radius: 12px;
          overflow: hidden;
          min-height: 180px;
        }
        .assigned-ticket-empty { padding: 20px; color: #5c677d; background: #fbfdff; }
        .assigned-ticket-table thead th {
          background: #f1f6fd;
          color: #355070;
          border-bottom: 1px solid #dbe5f3;
          font-size: 12px;
          letter-spacing: 0.03em;
          text-transform: uppercase;
          white-space: nowrap;
        }
        .assigned-ticket-table td { vertical-align: middle; color: #26354a; }
        .ticket-id-badge { color: #1f5c9f; font-weight: 700; }
        .ticket-description { min-width: 230px; white-space: pre-wrap; }
        .assigned-ticket-cards { padding: 12px; background: #f8fbff; }
        .assigned-ticket-card { background: #fff; border: 1px solid #dce7f5; border-radius: 10px; padding: 13px; margin-bottom: 10px; box-shadow: 0 2px 7px rgba(24, 55, 100, .06); }
        .assigned-ticket-card:last-child { margin-bottom: 0; }
        .assigned-card-top { display: flex; justify-content: space-between; gap: 8px; font-size: 12px; color: #68758b; }
        .assigned-card-description { margin: 11px 0; color: #26354a; line-height: 1.45; white-space: pre-wrap; }
        .assigned-card-details { display: flex; flex-direction: column; gap: 5px; font-size: 13px; color: #5d6b80; }
        .raised-ticket-card { background: #fff; border: 1px solid #dce7f5; border-left: 4px solid #4b7ac1; border-radius: 12px; margin-bottom: 12px; box-shadow: 0 3px 10px rgba(24, 55, 100, .06); }
        .raised-ticket-card-top { display: flex; justify-content: space-between; align-items: center; gap: 8px; margin-bottom: 12px; }
        .raised-ticket-status { border-radius: 50px; background: #fff4d8; color: #a15f00; padding: 4px 9px; font-size: 12px; font-weight: 700; }
        .raised-ticket-status.solved { background: #e6f5eb; color: #26824a; }
        @media (max-width: 576px) {
          .ticket-workspace { padding: 14px 10px 30px; }
          .ticket-hero { padding: 22px 18px; min-height: 0; border-radius: 14px; align-items: flex-start; flex-direction: column; }
          .ticket-hero h2 { font-size: 26px; }.ticket-hero p { font-size: 13px; line-height: 1.45; }.ticket-worker-chip { padding: 9px 12px; }
          .ticket-summary-grid { grid-template-columns: 1fr; gap: 10px; margin: 12px 0; }.ticket-summary-card { padding: 13px; }.ticket-summary-card strong { max-width: 235px; }
          .ticket-content-card { padding: 13px; border-radius: 14px; }
          .ticket-tabs { overflow-x: auto; gap: 0; margin-bottom: 14px; }
          .ticket-tab { flex: 1 0 auto; font-size: 13px; padding-left: 10px; padding-right: 10px; }
        }
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(10, 25, 47, 0.58);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }
        .ticket-create-modal {
          background: #fff;
          padding: 0;
          width: 100%;
          max-width: 640px;
          border-radius: 18px;
          position: relative;
          max-height: calc(100vh - 40px);
          overflow-y: auto;
          box-shadow: 0 24px 70px rgba(8, 26, 54, .32);
        }
        .ticket-modal-header { padding: 22px 64px 12px 26px; display: flex; align-items: center; gap: 13px; }
        .ticket-modal-icon { height: 42px; width: 42px; display: inline-flex; align-items: center; justify-content: center; border-radius: 12px; background: #e7f1ff; color: #2164b4; font-size: 20px; flex: 0 0 auto; }
        .ticket-modal-header h4 { font-size: 21px; margin: 0; color: #1b2b43; font-weight: 750; }
        .ticket-modal-title-wrap { min-width: 0; }.ticket-modal-header p { font-size: 13px; color: #667085; margin: 4px 0 0; line-height: 1.45; }
        .ticket-modal-close { position: absolute; right: 18px; top: 13px; border: 0; width: 34px; height: 34px; border-radius: 10px; background: #f2f5f9; color: #536277; font-size: 25px; line-height: 1; }
        .ticket-modal-close:hover { background: #edf1f6; color: #1f3048; }
        .ticket-modal-body { padding: 18px 26px 8px; }
        .ticket-readonly-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 18px; }
        .ticket-readonly-field { background: #f6f8fb; border: 1px solid #e5ebf3; border-radius: 10px; padding: 10px 12px; }
        .ticket-readonly-field span { display: block; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; color: #7b8798; }
        .ticket-readonly-field strong { display: block; margin-top: 3px; color: #26354a; font-size: 14px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ticket-form-field { margin-bottom: 17px; }
        .ticket-form-field > label { display: block; color: #34445d; font-size: 13px; font-weight: 700; margin-bottom: 7px; }
        .ticket-form-field em { color: #d34f4f; font-style: normal; }.ticket-form-field small { color: #8591a2; font-weight: 500; margin-left: 4px; }
        .ticket-form-field .form-control { border-radius: 10px; border-color: #d8e1ee; min-height: 43px; padding: 9px 12px; box-shadow: none; }
        .ticket-form-field textarea.form-control { min-height: 105px; resize: vertical; }
        .ticket-form-field .form-control:focus { border-color: #4c83c6; box-shadow: 0 0 0 3px rgba(76, 131, 198, .13); }
        .ticket-upload-box { min-height: 64px; display: flex !important; align-items: center; gap: 10px; padding: 12px; border: 1px dashed #8eaed1; background: #f7fbff; border-radius: 10px; cursor: pointer; color: #496889 !important; font-weight: 500 !important; }
        .ticket-upload-box i { color: #2468b8; font-size: 20px; }.ticket-upload-box span { font-size: 13px; }.ticket-upload-box input { display: none; }
        .ticket-modal-footer { padding: 16px 26px 22px; display: flex; justify-content: flex-end; gap: 10px; border-top: 1px solid #edf1f5; }
        .ticket-cancel-button { border: 1px solid #d7e0eb; color: #526176; border-radius: 9px; padding: 9px 18px; background: #fff; }.ticket-cancel-button:hover { background: #f5f7fa; }
        .ticket-submit-button { border: 0; color: #fff; border-radius: 9px; padding: 9px 18px; background: linear-gradient(135deg, #1a5da8, #347ec7); box-shadow: 0 5px 12px rgba(29, 98, 176, .22); }.ticket-submit-button:hover { color: #fff; background: linear-gradient(135deg, #154e8f, #2c70b2); }
        @media (max-width: 768px) {
          .ticket-create-modal { max-width: 100%; max-height: calc(100vh - 22px); border-radius: 14px; }
          .ticket-modal-header { padding: 18px 56px 10px 18px; align-items: flex-start; }.ticket-modal-body { padding: 16px 18px 5px; }.ticket-modal-footer { padding: 14px 18px 18px; }.ticket-readonly-grid { grid-template-columns: 1fr; gap: 8px; }
          nav .navbar-brand img {
            height: 35px;
          }
          nav span {
            font-size: 14px;
          }
          .dashboard-container {
            padding: 5px;
          }
        }
      `}</style>
    </div>
  );
}

export default UserTickets;
